<template>
  <div class="relative isolate -mb-8">
    <RevTabs
      class="mx-auto flex w-fit max-w-fit"
      :label="i18n(translations.title)"
      role="navigation"
      variant="flexible"
    >
      <RevTabItem
        :active="intersecting === 'overview'"
        :label="i18n(translations.overview)"
        role="tab"
        @click.prevent="handleClick('overview')"
      />
      <RevTabItem
        :active="intersecting === 'product-feature'"
        :label="i18n(translations.productFeature)"
        role="tab"
        @click.prevent="handleClick('product-feature')"
      />
      <RevTabItem
        v-if="reviewCount"
        :active="intersecting === 'reviews'"
        :label="i18n(translations.reviews)"
        role="tab"
        @click.prevent="handleClick('reviews', 'start')"
      >
        <RevBadge :count="reviewCount" variant="info" />
      </RevTabItem>
      <RevTabItem
        :active="intersecting === 'warranty'"
        :label="i18n(translations.warranty)"
        role="tab"
        @click.prevent="handleClick('warranty')"
      />
      <RevTabItem
        :active="intersecting === 'about'"
        :label="i18n(translations.about)"
        role="tab"
        @click.prevent="handleClick('about')"
      />
    </RevTabs>
    <div
      class="border-static-default-low absolute bottom-8 left-0 right-0 h-2 max-w-full border-b"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevBadge } from '@ds/components/Badge'
import { RevTabItem } from '@ds/components/TabItem'
import { RevTabs } from '@ds/components/Tabs'

import translations from './TabNavigation.translations'

const i18n = useI18n()

defineProps<{ reviewCount: number }>()

const observer = ref<IntersectionObserver | null>(null)

const intersecting = ref<string>('')

function handleClick(section: string, position: 'start' | 'center' = 'center') {
  // Using getElementById instead of ref as the target is in another component
  document.getElementById(section)?.scrollIntoView({
    behavior: 'smooth',
    // Centering the block in the page to avoid sticky elements hidden the first lines.
    block: position,
  })
}

onMounted(() => {
  const headerId = window.matchMedia('(min-width: 768px)').matches
    ? 'JapanProductPageStickyBarTopRoot'
    : 'JapanProductPageStickyBarTopSmallRoot'
  const header = document.querySelector(headerId)

  observer.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          intersecting.value = entry.target.id
        }
      })
    },
    {
      root: header,
      threshold: 0.1,
    },
  )

  const ids = ['overview', 'product-feature', 'reviews', 'warranty', 'about']
  ids.forEach((id) => {
    const element = document.getElementById(id)
    if (element) {
      observer.value?.observe(element)
    }
  })
})

onBeforeUnmount(() => {
  observer.value?.disconnect()
})
</script>
